import { CHAIN } from '@tonconnect/protocol'

/**
 * Gets the configured TON chain from build environment.
 * Returns CHAIN.TESTNET for "-3" or CHAIN.MAINNET for "-239"
 */
function getConfiguredChain(): CHAIN {
  const tonChain = process.env.TON_CHAIN

  // This validation should have been caught at build time
  if (tonChain !== '-3' && tonChain !== '-239') {
    throw new Error(
      `CRITICAL: Invalid TON_CHAIN value: "${tonChain}". Must be "-3" or "-239". ` +
        'This should have been caught during build.',
    )
  }

  return tonChain === '-3' ? CHAIN.TESTNET : CHAIN.MAINNET
}

/**
 * Validates that the connected wallet network matches the build environment.
 * Throws an error if there's a mismatch.
 *
 * @param network - The network from TON Connect wallet
 * @throws Error if wallet network doesn't match configured chain
 */
export function validateNetworkMatch(network: CHAIN | null): void {
  if (!network) return // Wallet not connected, nothing to validate

  const configuredChain = getConfiguredChain()

  if (network !== configuredChain) {
    const networkName = network === CHAIN.MAINNET ? 'Mainnet' : 'Testnet'
    const expectedName =
      configuredChain === CHAIN.MAINNET ? 'Mainnet' : 'Testnet'

    throw new Error(
      `CRITICAL: ${networkName} wallet connected to ${expectedName} build! ` +
        `Please disconnect your wallet and connect a ${expectedName} wallet.`,
    )
  }
}

/**
 * Checks if the current network is testnet.
 * When wallet is not connected, uses the build environment.
 *
 * @param network - The network from TON Connect wallet
 * @returns true if testnet, false if mainnet
 */
export function isTestnet(network: CHAIN | null): boolean {
  // If wallet is connected, validate it matches build environment
  if (network) {
    validateNetworkMatch(network)
    return network === CHAIN.TESTNET
  }

  // Wallet not connected - use build environment
  return getConfiguredChain() === CHAIN.TESTNET
}

/**
 * Checks if the current network is mainnet.
 * When wallet is not connected, uses the build environment.
 *
 * @param network - The network from TON Connect wallet
 * @returns true if mainnet, false if testnet
 */
export function isMainnet(network: CHAIN | null): boolean {
  // If wallet is connected, validate it matches build environment
  if (network) {
    validateNetworkMatch(network)
    return network === CHAIN.MAINNET
  }

  // Wallet not connected - use build environment
  return getConfiguredChain() === CHAIN.MAINNET
}
